import {
  ArrowLeft,
  ArrowsInSimple,
  ArrowSquareOut,
  CheckCircle,
  X,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useRouter } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  AssetViewerPane,
  type AssetFile,
  type CitationTarget,
} from "./AssetViewerPane";
import { PaneHeader } from "./PaneHeader";
import { ReviewNavStrip } from "./ReviewNavStrip";

import { ticketRoute } from "../../tickets/routes/ticket/route";
import { useTicketViewData } from "../hooks/useTicketView";
import {
  visibleReviewComments,
  reviewCommentsForPanel,
} from "../utils/visibleComments";

interface TicketViewPaneProps {
  ticketId: string;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  /** Close the artifact viewer (cowork sidebar mode). */
  onClose?: () => void;
  reviewStatus?: string | null;
  onCitationClick?: (citation: CitationTarget) => void;
  /**
   * Sequential review navigation. When provided alongside totalReviews > 1,
   * a "Review N of M" strip renders below the header. Only set on the
   * sequential single-review TicketViewPane instance — not the project
   * drill-in instance.
   */
  reviewIndex?: number;
  totalReviews?: number;
  onPrevReview?: () => void;
  onNextReview?: () => void;
  /**
   * True while the Vitalora killer-flow typewriter is still revealing
   * the parallel review. When set, the asset image renders without any
   * pins or comment carousel and the header swaps the resolved status
   * badges for a "Reviewing…" pill. Findings appear together once the
   * chat narration finishes — they must NOT spoil the reveal at t=0.
   *
   * Mirrors `isKillerFlowStreaming` on `ProjectStatusPane` so single
   * and multi-asset killer-flow chats share the same gate.
   */
  isKillerFlowStreaming?: boolean;
}

const STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

export function TicketViewPane({
  ticketId,
  onCollapse,
  onBackToProject,
  onClose,
  reviewStatus,
  onCitationClick,
  reviewIndex,
  totalReviews,
  onPrevReview,
  onNextReview,
  isKillerFlowStreaming = false,
}: TicketViewPaneProps) {
  const router = useRouter();
  // Build through the router so the URL respects the configured basepath
  // (e.g. `/ticket-canvas-shapes-guard` on epic environments). A raw
  // `/tickets/...` href bypasses the SPA's basepath and lands the
  // new tab on the wrong app root.
  const ticketHref = router.buildLocation({
    to: ticketRoute.to,
    params: { ticketId },
    search: { version: 0 },
  }).href;
  const { ticket, comments, isLoading } = useTicketViewData(ticketId);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  // Viewer page nav + zoom/rotate toolbar, reported up from AssetViewerPane so
  // it can live in this pane's own header instead of a footer.
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTicketIdRef = useRef(ticketId);
  const [artworkHeaderControls, setArtworkHeaderControls] =
    useState<ReactNode | null>(null);
  const [includeRejectedComments, setIncludeRejectedComments] = useState(false);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
      }
    },
    [],
  );

  const latestBoard = ticket?.revisionBoards
    ?.filter((b) => !b.deletedAt)
    .at(-1);
  const reviewFiles = latestBoard?.reviewFiles ?? [];
  const thumbnails = ticket?.thumbnails ?? [];

  // Defense-in-depth: hide soft-deleted and reviewer-rejected comments so
  // pins and the comment carousel stay consistent with CommentsList.
  const activeComments = useMemo(
    () => visibleReviewComments(comments),
    [comments],
  );
  const commentsForSelection = useMemo(
    () => reviewCommentsForPanel(comments, includeRejectedComments),
    [comments, includeRejectedComments],
  );

  useEffect(() => {
    if (prevTicketIdRef.current === ticketId) return;
    prevTicketIdRef.current = ticketId;
    setSelectedCommentId(null);
    setHighlightPing(null);
    setIncludeRejectedComments(false);
  }, [ticketId]);

  // Mirror ReviewArtworkContextColumns: spotlight zoom/mask need an active
  // comment on first paint once comments land (2-pane chat reviews use this
  // pane instead of the 3-pane ReviewArtworkColumn).
  useEffect(() => {
    if (isKillerFlowStreaming) return;
    if (!selectedCommentId && activeComments.length) {
      setSelectedCommentId(activeComments[0]._id ?? null);
    }
  }, [selectedCommentId, activeComments, isKillerFlowStreaming]);

  useEffect(() => {
    if (isKillerFlowStreaming) return;
    if (!selectedCommentId) return;
    const stillVisible = commentsForSelection.some(
      (c) => c._id === selectedCommentId,
    );
    if (!stillVisible) {
      setSelectedCommentId(commentsForSelection[0]?._id ?? null);
    }
  }, [selectedCommentId, commentsForSelection, isKillerFlowStreaming]);

  // Stabilize the assets array identity: ImageViewer resets scroll position
  // when `assets` changes, so an unstable reference on every poll can snap
  // the viewport back to the top mid-scroll. Memo must run unconditionally
  // (no early returns above) to satisfy the rules of hooks.
  const assets: AssetFile[] = useMemo(() => {
    if (reviewFiles.length > 0) return reviewFiles;
    return thumbnails.map((src, i) => ({
      source: src,
      name: `Page ${i + 1}`,
    }));
  }, [reviewFiles, thumbnails]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Flex direction="column" align="center" gap="2">
          <Spinner size="3" />
          <Text size="2" className="text-puntt-neutral-gray-9">
            Loading ticket...
          </Text>
        </Flex>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          Ticket not found
        </Text>
      </div>
    );
  }

  const status = ticket.status ?? "wip";

  return (
    <div
      className="flex h-full flex-col bg-base-white"
      data-testid="ticket-view-pane"
    >
      <PaneHeader
        region="ticket"
        title={
          <span className="truncate text-[13px] font-semibold normal-case tracking-normal text-puntt-neutral-gray-12">
            {ticket.title}
          </span>
        }
        leading={
          onBackToProject ? (
            <Button
              data-testid="ticket-view-pane-button-173"
              size="1"
              variant="ghost"
              onClick={onBackToProject}
              className="shrink-0 cursor-pointer"
              aria-label="Back to project"
            >
              <ArrowLeft size={14} />
            </Button>
          ) : undefined
        }
        trailing={
          <Flex align="center" gap="1" className="shrink-0">
            {isKillerFlowStreaming ? (
              <Badge
                size="1"
                variant="soft"
                color="amber"
                data-testid="ticket-view-pane-reviewing-pill"
              >
                <span className="mr-1 inline-block size-1.5 animate-pulse rounded-full bg-puntt-gold-9 align-middle" />
                Reviewing…
              </Badge>
            ) : (
              <>
                {(!latestBoard?.isAIReviewed || status !== "wip") && (
                  <Badge
                    size="1"
                    variant="soft"
                    color={STATUS_COLORS[status] ?? "gray"}
                  >
                    {STATUS_LABELS[status] ?? status}
                  </Badge>
                )}
                {latestBoard?.isAIReviewed && (
                  <Badge size="1" variant="soft" color="blue">
                    AI Reviewed
                  </Badge>
                )}
              </>
            )}
            {reviewStatus &&
              !isKillerFlowStreaming &&
              (reviewStatus === "Review complete" ? (
                <>
                  <Badge
                    size="1"
                    variant="soft"
                    color="green"
                    data-testid="ticket-view-pane-review-complete-badge"
                  >
                    <CheckCircle size={12} weight="fill" aria-hidden="true" />
                    Review complete
                  </Badge>
                  <Button
                    size="1"
                    variant="soft"
                    color="green"
                    asChild
                    className="cursor-pointer"
                  >
                    <a
                      data-testid="ticket-view-pane-view-review-cta"
                      href={ticketHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View your review
                    </a>
                  </Button>
                </>
              ) : (
                <Text
                  size="1"
                  className="animate-pulse italic text-puntt-accent-9"
                >
                  {reviewStatus}
                </Text>
              ))}
            <Flex
              align="center"
              gap="2"
              className="shrink-0"
              data-testid="ticket-view-pane-header-controls"
            >
              {artworkHeaderControls}
              <Tooltip content="Open in full review">
                <Button
                  size="1"
                  variant="ghost"
                  asChild
                  className="cursor-pointer"
                  aria-label="Open in full review"
                >
                  <a
                    data-testid="ticket-view-pane-a-224"
                    href={ticketHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ArrowSquareOut size={14} />
                  </a>
                </Button>
              </Tooltip>
              {onClose ? (
                <Tooltip content="Close review">
                  <IconButton
                    data-testid="chat-artifact-viewer-close"
                    size="1"
                    variant="ghost"
                    color="gray"
                    onClick={onClose}
                    className="cursor-pointer"
                    aria-label="Close review"
                  >
                    <X size={14} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {onCollapse && (
                <Tooltip content="Collapse pane">
                  <IconButton
                    data-testid="ticket-view-pane-button-231"
                    size="1"
                    variant="ghost"
                    color="gray"
                    onClick={onCollapse}
                    className="cursor-pointer"
                    aria-label="Collapse pane"
                  >
                    <ArrowsInSimple size={14} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        }
      />

      <ReviewNavStrip
        reviewIndex={reviewIndex ?? 0}
        totalReviews={totalReviews ?? 0}
        onPrevReview={onPrevReview}
        onNextReview={onNextReview}
      />

      {/* Body */}
      <div className="min-h-0 flex-1">
        <AssetViewerPane
          // Reset internal viewer state (incl. Office snapshot) per ticket so
          // the spreadsheet → image transition on one ticket can't carry over
          // to the next when this pane is reused for a different ticket.
          key={ticketId}
          title=""
          assets={assets}
          comments={isKillerFlowStreaming ? [] : activeComments}
          allReviewComments={isKillerFlowStreaming ? [] : comments}
          includeRejectedComments={includeRejectedComments}
          onIncludeRejectedCommentsChange={setIncludeRejectedComments}
          onCitationClick={onCitationClick}
          activeCommentId={isKillerFlowStreaming ? null : selectedCommentId}
          onCommentClick={
            isKillerFlowStreaming ? undefined : handleCommentClick
          }
          onPinClick={isKillerFlowStreaming ? undefined : handleCommentClick}
          highlightPosition={isKillerFlowStreaming ? null : highlightPing}
          ticketId={ticketId}
          viewerToolbarPlacement="external"
          onHeaderControlsChange={setArtworkHeaderControls}
          commentComposePlacement="inline"
        />
      </div>
    </div>
  );
}
